import type { SelectorLocation } from '../../../../apis/locationTasks/locationTask.types';

const MAX_LOCATION_SELECTOR_DISPLAY_LENGTH = 50;

export interface LocationDisplayValue {
    id: string;
    name: string;
    openingDate?: unknown;
}

// Merge Redux-persisted and URL-provided location keys, removing duplicates.
// URL keys must be included even when Redux state hasn't caught up yet.
export const mergeLocationKeys = (reduxKeys: string[] = [], urlKeys: string[] = []): string[] =>
    [...new Set([...(reduxKeys ?? []), ...(urlKeys ?? [])])];

// The default location only applies when neither Redux nor the URL selected one.
// "All locations" mode deliberately selects no keys, so it must not fall back either.
export const shouldApplyDefaultLocation = (
    reduxKeys: string[] = [],
    urlKeys: string[] = [],
    defaultLocationKey?: string | null,
    isAllLocations = false
): boolean =>
    !isAllLocations &&
    (reduxKeys?.length ?? 0) === 0 &&
    (urlKeys?.length ?? 0) === 0 &&
    typeof defaultLocationKey === 'string' &&
    defaultLocationKey.length > 0;

// Label shown on the location selector button.
export const getSelectedLocationsLabel = (
    selectedLocationKeys: string[] = [],
    locations: LocationDisplayValue[] = [],
    isAllLocations = false
): string => {
    if (isAllLocations && (selectedLocationKeys?.length ?? 0) === 0) {
        return 'All Locations';
    }

    if (!locations?.length || !selectedLocationKeys?.length) return '';

    const selectedLocations = locations.filter((m) => selectedLocationKeys.indexOf(m.id) >= 0);

    if (selectedLocations.length > 3) {
        return selectedLocations.length + ' Locations Displayed';
    }

    let retval = '';
    for (let i = 0; i < selectedLocations.length; i++) {
        retval += selectedLocations[i].name + ' ';
        if (i !== selectedLocations.length - 1) {
            retval += ', ';
        }
    }
    return retval;
};

export const buildLocationsDisplayValues = (
    locations: SelectorLocation[] = []
): LocationDisplayValue[] => {
    if (!locations || locations.length === 0) return [];

    return locations.map((x) => {
        const ellipsis = '...';
        const maxDisplayValueLength = MAX_LOCATION_SELECTOR_DISPLAY_LENGTH - ellipsis.length;
        let displayValue = `${x.name} (${x.abbreviation})`;
        if (displayValue.length > maxDisplayValueLength) {
            const diff = displayValue.length - maxDisplayValueLength;
            const truncatedLength = x.name.length - diff;
            const truncatedName = x.name.substring(0, truncatedLength).trimEnd();
            displayValue = `${truncatedName}${ellipsis} (${x.abbreviation})`;
        }
        // Keep extra location info that we need elsewhere on the task list
        return { id: x.locationKey, name: displayValue, openingDate: x.openingDate };
    });
};
